import React, { useState, useEffect } from "react";
import { Nav } from "react-bootstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HiOutlineHome } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../store/slices/auth";

import {
  MdOutlinePayments,
  MdOutlineAssessment,
  MdOutlineAccountBalanceWallet,
} from "react-icons/md";

import {
  FaRegMoneyBill1,
  FaSchool,
  FaBus,
} from "react-icons/fa6";


import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
} from "react-icons/fi";
import logoone from "../../assets/favicon.png";
import logotwo from "../../assets/logo-white.png";

const NAV_ITEMS = [
  {
    key: "home",
    label: "Home",
    icon: HiOutlineHome,
    to: "/accounts/home",
  },

  {
    key: "openingbalance",
    label: "Opening Balance",
    icon: MdOutlineAccountBalanceWallet,
    to: "/accounts/opening-balance",
  },

  {
    key: "expense",
    label: "Expense",
    icon: FaRegMoneyBill1,
    to: "/accounts/expense",
  },

  {
    key: "fees",
    label: "Fees Collection",
    icon: MdOutlinePayments,
    children: [
      {
        label: "Tuition Fees",
        icon: FaSchool,
        to: "/accounts/fees-collection",
      },
      // {
      //   label: "Uniform Fees",
      //   icon: MdCurrencyRupee,
      //   to: "/accounts/fee-structure",
      // },
      // {
      //   label: "Books Fees",
      //   icon: PiBookOpenTextLight,
      //   to: "/accounts/books-fees",
      // },
      {
        label: "Transportation Fees",
        icon: FaBus,
        to: "/accounts/transport-fees-collection",
      },
    ],
  },

  // {
  //   key: "receipts",
  //   label: "Receipts",
  //   icon: MdOutlineReceiptLong,
  //   to: "/accounts/receipts",
  // },

  {
    key: "reports",
    label: "Reports",
    icon: MdOutlineAssessment,
    to: "/accounts/reports",
  },
];

export default function SideNavbar({
  collapsed,
  setCollapsed,
  mobileNavOpen,
  setMobileNavOpen,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const closeMobile = () => setMobileNavOpen(false);

  const isChildActive = (children) =>
    children?.some((child) => location.pathname.startsWith(child.to));

  useEffect(() => {
    const activeParent = NAV_ITEMS.find(
      (item) => item.children && isChildActive(item.children)
    );
    setOpenSubmenu(activeParent ? activeParent.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (collapsed) setOpenSubmenu(null);
  }, [collapsed]);

  // Clicking ANY icon while collapsed should expand the sidebar first.
  // - Plain link items: expand + navigate.
  // - Parent (with children) items: expand + open that submenu.
  const handleParentClick = (item) => {
    if (collapsed) {
      setCollapsed(false);
      if (item.children) setOpenSubmenu(item.key);
      return;
    }
    setOpenSubmenu((prev) => (prev === item.key ? null : item.key));
  };

  const handlePlainLinkClick = (item, e) => {
    if (collapsed) {
      e.preventDefault();
      setCollapsed(false);
      // Give the expand animation a tick before navigating so the
      // click doesn't feel like it "did nothing" visually.
      navigate(item.to);
      return;
    }
    closeMobile();
  };

  const dispatch = useDispatch();

  const { profile } = useSelector((state) => state.auth);

  const instituteLogo = profile?.instituteId?.instituteLogo;


  useEffect(() => {
    if (!profile) {
      dispatch(getProfile());
    }
  }, [dispatch, profile]);


  return (
    <aside
      className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileNavOpen ? "mobile-open" : ""
        }`}
    >
      <div className="sidebar-mobile-close">
        <button onClick={closeMobile}>×</button>
      </div>

      <div className="sidebar-header text-light">
        <div className="sidebar-logo">
          <img
            src={
              instituteLogo
                ? instituteLogo
                : collapsed
                  ? logoone
                  : logotwo
            }
            alt="Institute Logo"
            className={`sidebar-logo-img ${collapsed ? "sidebar-logo-mini" : "sidebar-logo-full"
              }`}
          />
        </div>
        <button
          className="toggle-btn"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
        </button>
      </div>

      <div className="sidebar-menu">
        <Nav className="flex-column sidebar-nav">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;

            if (item.children) {
              const childActive = isChildActive(item.children);
              const isOpen = openSubmenu === item.key;

              return (
                <div className={`nav-group ${isOpen ? "open" : ""}`} key={item.key}>
                  <div
                    className={`nav-link nav-parent ${childActive ? "active" : ""}`}
                    onClick={() => handleParentClick(item)}
                    data-tooltip={item.label}
                  >
                    <Icon size={18} />
                    {!collapsed && <span className="nav-label">{item.label}</span>}
                    {!collapsed && (
                      <FiChevronDown
                        size={14}
                        className={`submenu-caret ${isOpen ? "rotated" : ""}`}
                      />
                    )}
                    {collapsed && childActive && <span className="active-dot" />}
                  </div>

                  <div
                    className="submenu-wrapper"
                    style={{
                      maxHeight:
                        isOpen && !collapsed
                          ? `${item.children.length * 44}px`
                          : "0px",
                    }}
                  >
                    <div className="submenu">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={closeMobile}
                          className={({ isActive }) =>
                            `submenu-link ${isActive ? "active" : ""}`
                          }
                        >
                          <span className="submenu-dot" />
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="nav-group" key={item.key}>
                <NavLink
                  to={item.to}
                  onClick={(e) => handlePlainLinkClick(item, e)}
                  data-tooltip={item.label}
                  className={({ isActive }) =>
                    `nav-link ${isActive ? "active" : ""}`
                  }
                >
                  <Icon size={18} />
                  {!collapsed && <span className="nav-label">{item.label}</span>}
                </NavLink>
              </div>
            );
          })}
        </Nav>
      </div>
    </aside>
  );
}